import { prop, removeVat, round, slugify } from './utilities';

export const preparePromotionForMarketing = (
  affiliation: string,
  banner: any,
  items?: unknown,
): any => {
  if (!banner) {
    return null;
  }
  const promotionObject: any = {
    creative_name: prop(banner, 'b_description') || '',
    creative_slot: prop(banner, 'b_position'),
    promotion_id: prop(banner, 'id'),
    promotion_name: prop(banner, 'b_name'),
  };
  if (items && Array.isArray(items) && items.length) {
    promotionObject.items = items
      .map((item, i) => {
        return prepareProductForMarketing(affiliation, item, null, {
          quantity: prop(item, 'quantity') || prop(item, 'count'),
          idx: i,
        });
      })
      .filter(a => a);
  }

  return promotionObject;
};

export const prepareOrderForMarketing = (
  affiliation: string,
  order: any,
  dataLayerProducts?: any,
  includeUserData = false,
  additionalOrderData?: any,
): any => {
  if (!order) {
    return null;
  }

  const tax =
    prop(order, 'order_tprice') - prop(order, 'order_tprice_without_vat');
  let purchaseValue = prop(order, 'total_items_price.priceWithoutVat');
  if (!purchaseValue) {
    const totalPrice =
      prop(order, 'order_tprice') -
      (prop(order, 'delivery.delivery_price.price') ||
        prop(order, 'delivery.delivery_price')) -
      (prop(order, 'payment.payment_price.price') ||
        prop(order, 'payment.payment_price'));
    purchaseValue = removeVat(totalPrice, prop(order, 'vat_rate'), false);
  }

  const orderObject: any = {
    transaction_id: prop(order, 'order_nr'),
    value: round(purchaseValue, 4),
    tax: round(tax, 2),
    shipping: removeVat(
      (prop(order, 'delivery.delivery_price.price') ||
        prop(order, 'delivery.delivery_price')) +
        (prop(order, 'payment.payment_price.price') ||
          prop(order, 'payment.payment_price')),
      prop(order, 'delivery.vat_rate'),
      true,
      2,
    ),
    currency: prop(order, 'order_tpricecurrency'),
    coupon: prop(order, 'payload.giftcards.0.card_number') || '',
    items: (prop(order, 'items') || [])
      .map((item, i) => {
        return prepareProductForMarketing(
          affiliation,
          item,
          dataLayerProducts,
          {
            quantity: prop(item, 'quantity') || prop(item, 'count'),
            idx: i,
          },
        );
      })
      .filter(a => a),
  };

  if (includeUserData) {
    orderObject.email = prop(order, 'address.person.email');
    orderObject.phone_number = prop(order, 'address.person.phone');
    orderObject.address = {
      first_name: prop(order, 'address.person.name'),
      last_name: prop(order, 'address.person.surname'),
      street: prop(order, 'address.street'),
      city: prop(order, 'address.city'),
      postal_code: prop(order, 'address.zip'),
      country: prop(order, 'address.country_code'),
    };
  }

  if (order && order.is_returning_customer !== undefined) {
    orderObject.new_customer = !prop(order, 'is_returning_customer');
  }

  return additionalOrderData
    ? { ...additionalOrderData, ...orderObject }
    : orderObject;
};

export const prepareCartForMarketing = (
  affiliation: string,
  cart: any,
  cartItem?: any,
  dataLayerProducts?: any,
  additionalData?: {
    payment_type?: any;
    shipping_tier?: any;
    quantity?: number;
    item_list_id?: string;
    item_list_name?: string;
    idx?: number;
    isRemoveFromCart?: boolean;
  },
  isItem = false,
): any => {
  const preparedProduct = cartItem
    ? prepareProductForMarketing(affiliation, cartItem, dataLayerProducts, {
        quantity: prop(additionalData, 'quantity'),
        good: prop(cartItem, 'good'),
        item_list_name: prop(additionalData, 'item_list_name'),
        idx: prop(additionalData, 'idx'),
        includeNotRoundedPrice: isItem,
        isRemoveFromCart: prop(additionalData, 'isRemoveFromCart'),
      })
    : null;
  const notRoundedPrice = prop(preparedProduct, 'notRoundedPrice');
  if (notRoundedPrice) {
    delete preparedProduct.notRoundedPrice;
  }

  let cartValue = isItem
    ? notRoundedPrice
      ? round(notRoundedPrice * prop(preparedProduct, 'quantity'), 2)
      : round(
          prop(preparedProduct, 'price') * prop(preparedProduct, 'quantity'),
          2,
        )
    : round(
        prop(cart, 'total_items_price.priceWithoutVat') ||
          prop(cart, 'total_items_price_without_vat') ||
          prop(cart, 'total_price_without_vat'),
        4,
      );
  if (!cartValue) {
    const totalPrice =
      prop(cart, 'order_tprice') -
      (prop(cart, 'delivery.delivery_price.price') ||
        prop(cart, 'delivery.delivery_price')) -
      (prop(cart, 'payment.payment_price.price') ||
        prop(cart, 'payment.payment_price'));
    cartValue = removeVat(totalPrice, prop(cart, 'vat_rate'), false);
  }
  const cartObject: any = {
    currency: prop(cart, 'currency') || prop(cart, 'order_tpricecurrency'),
    value: round(cartValue, 4),
    coupon:
      prop(cart, 'giftcards.0.card_number') || prop(cart, 'giftcards.0') || '',
    items: cartItem
      ? [preparedProduct].filter(a => a)
      : (prop(cart, 'items') || [])
          .map((item, i) => {
            return prepareProductForMarketing(
              affiliation,
              item,
              dataLayerProducts,
              {
                quantity:
                  prop(additionalData, 'quantity') ||
                  prop(item, 'quantity') ||
                  prop(item, 'count'),
                idx: i,
                item_list_id: prop(additionalData, 'item_list_id'),
              },
            );
          })
          .filter(a => a),
  };

  if (prop(additionalData, 'shipping_tier')) {
    cartObject.shipping_tier = prop(additionalData, 'shipping_tier');
  }
  if (prop(additionalData, 'payment_type')) {
    cartObject.payment_type = prop(additionalData, 'payment_type');
  }
  if (!prop(cartObject, 'items.length')) {
    return null;
  }
  return cartObject;
};

export const prepareItemListForMarketing = (
  affiliation: string,
  items?: any,
  dataLayerProducts?: any,
  additionalData?: {
    item_list_id?: string;
    item_list_name?: string;
    item_variant?: string;
    location_id?: string;
    quantity?: number;
    idx?: number;
    good?: any;
  },
): any => {
  if (!additionalData || !items || !items.length) {
    return null;
  }
  const itemObject: any = {
    item_list_id:
      prop(additionalData, 'item_list_id') ||
      slugify(prop(additionalData, 'item_list_name')),
    item_list_name: prop(additionalData, 'item_list_name'),
    items: items
      .map((item, i) => {
        return prepareProductForMarketing(
          affiliation,
          item,
          dataLayerProducts,
          {
            ...additionalData,
            idx:
              prop(additionalData, 'idx') || prop(additionalData, 'idx') === 0
                ? prop(additionalData, 'idx')
                : i,
          },
        );
      })
      .filter(a => a),
  };

  if (!prop(itemObject, 'items.length')) {
    return null;
  }

  return itemObject;
};

export const prepareProductForMarketing = (
  affiliation: string,
  product: any,
  dataLayerProducts?: any,
  additionalData?: {
    item_list_id?: string;
    item_list_name?: string;
    item_variant?: string;
    location_id?: string;
    quantity?: number;
    idx?: number;
    good?: any;
    includeNotRoundedPrice?: boolean;
    isRemoveFromCart?: boolean;
  },
): any => {
  if (!product || prop(product, 'is_meta_item')) {
    return null;
  }
  // we use this if we are sending product from application already remapped
  if (product.noRemap) {
    return product;
  }

  const heliosUnits =
    prop(product, 'product_detail.main_good.helios_units') ||
    prop(product, 'main_good.helios_units') ||
    prop(product, 'good.helios_units') ||
    prop(product, 'helios_units');
  const isMks = heliosUnits === 'Mks';

  const good =
    prop(additionalData, 'good') ||
    prop(product, 'good') ||
    prop(product, 'good_details_json');
  const param1 =
    prop(good, 'marketingParams.param1') ||
    prop(product, 'good.marketingParams.param1') ||
    prop(product, 'marketingParams.param1') ||
    prop(product, 'main_good.marketingParams.param1');
  const productId =
    prop(product, 'item.productId') ||
    prop(product, 'product.product_id') ||
    prop(product, 'product_id') ||
    prop(good, 'product_id');
  const itemId =
    prop(product, 'plu') ||
    prop(product, 'product_plu') ||
    prop(product, 'productPlu') ||
    prop(product, 'good_details_json.productPlu') ||
    prop(product, 'product.plu') ||
    productId;
  const goodId =
    prop(good, 'good_id') ||
    prop(product, 'main_good.good_id') ||
    prop(product, 'good_id') ||
    prop(product, 'item.goodId') ||
    prop(product, 'product_detail.main_good.good_id') ||
    '';
  const itemVariant =
    prop(good, 'good_id') ||
    prop(good, 'color') ||
    prop(product, 'product_detail.main_good.good_id') ||
    prop(product, 'main_good.good_id') ||
    prop(product, 'main_good.color') ||
    prop(product, 'product_detail.main_good.color') ||
    prop(product, 'good_id') ||
    goodId ||
    '';

  const itemName =
    prop(product, 'product.publish.0.product_name') ||
    prop(product, 'product_detail.publish.0.product_name') ||
    prop(product, 'publish.0.product_name') ||
    prop(product, 'product.name') ||
    prop(product, 'product_name') ||
    prop(product, 'name');

  let brandName =
    prop(product, 'product.brand.brand_name') ||
    prop(product, 'brand.brand_name') ||
    prop(product, 'brand') ||
    prop(product, 'good_details_json.brand_name');
  if (typeof brandName === 'object') {
    brandName = '';
  }
  const index = prop(additionalData, 'idx') || 0;
  const quantity =
    prop(additionalData, 'quantity') ||
    prop(product, 'quantity') ||
    prop(product, 'count') ||
    1;
  const quantityForPrice = quantity;
  const categoryPathString =
    prop(product, 'good_details_json.resolved_category_path') ||
    prop(product, 'product.publish.0.resolved_category_path') ||
    prop(product, 'resolved_category_path') ||
    '';
  const categoryPath = categoryPathString.split(' | ');
  const mainCategory =
    categoryPath && categoryPath.length > 0
      ? categoryPath[categoryPath.length - 1]
      : '';
  let category =
    prop(product, 'category_text') ||
    mainCategory ||
    prop(product, 'category') ||
    '';
  const category2 = prop(product, 'category2') || '';
  const currency =
    prop(good, 'currency_id') ||
    prop(product, 'product_detail.main_good.currency_id') ||
    prop(product, 'main_good.currency_id') ||
    prop(product, 'main_good.currency') ||
    prop(good, 'currency') ||
    prop(product, 'good.currency_id') ||
    prop(product, 'currency');
  const vatRate =
    prop(good, 'vat_rate') ||
    prop(product, 'product_detail.main_good.vat_rate') ||
    prop(product, 'main_good.vat_rate') ||
    prop(product, 'main_good.vat_rate') ||
    prop(product, 'vat_rate');
  const oldPrice =
    prop(good, 'oldprice') ||
    prop(product, 'product_detail.main_good.oldprice') ||
    prop(product, 'main_good.oldprice');
  let finalPrice = prop(product, 'order_id')
    ? prop(product, 'totalprice') / quantity
    : prop(good, 'final_price') ||
      prop(product, 'product_detail.main_good.final_price') ||
      prop(product, 'main_good.final_price') ||
      prop(product, 'main_good_price');
  if (!finalPrice) {
    finalPrice = prop(product, 'unitprice');
  }
  const cartUnitPriceWithoutVat = prop(additionalData, 'isRemoveFromCart')
    ? null
    : prop(product, 'sum_price_without_vat')
      ? prop(product, 'sum_price_without_vat') / quantityForPrice
      : null;

  let finalPriceWithoutVat = prop(product, 'order_id')
    ? prop(product, 'totalprice_without_vat') / quantityForPrice
    : cartUnitPriceWithoutVat
      ? round(cartUnitPriceWithoutVat, 4)
      : prop(product, 'item.productId')
        ? round(prop(product, 'priceWithoutVat'), 4)
        : prop(good, 'final_price_without_vat') ||
          prop(product, 'product_detail.main_good.final_price_without_vat') ||
          prop(product, 'main_good.final_price_without_vat') ||
          prop(product, 'main_good_price_without_vat');
  if (!finalPriceWithoutVat) {
    finalPriceWithoutVat = removeVat(finalPrice, vatRate, true, 4);
  }
  const oldPriceWithoutVat = round(
    oldPrice > 0 ? removeVat(oldPrice, vatRate) : finalPriceWithoutVat,
    2,
  );
  let discountWithVat = oldPrice - finalPrice;
  if (discountWithVat <= 0) {
    const salePercentage =
      prop(good, 'recalculated_sale_percentage') ||
      prop(product, 'product_detail.main_good.recalculated_sale_percentage') ||
      prop(product, 'main_good.recalculated_sale_percentage');
    if (salePercentage > 5 && salePercentage < 90) {
      discountWithVat = finalPrice * (1 + salePercentage / 100) - finalPrice;
    }
  }

  const key = `${productId}-${goodId}`;
  const savedProduct = dataLayerProducts && dataLayerProducts[`${key}`];
  const itemListName =
    prop(savedProduct, 'item_list_name') ||
    prop(additionalData, 'item_list_name') ||
    '';
  const itemListId = slugify(itemListName) || '';

  if (!category) {
    category = prop(savedProduct, 'category') || '';
  }

  // if (isMks && !cartUnitPriceWithoutVat) {
  //   finalPriceWithoutVat = finalPriceWithoutVat * 1000;
  // }

  if (!finalPriceWithoutVat) {
    return null;
  }

  const finalObject: any = {
    item_id: itemId,
    item_name: itemName,
    affiliation,
    coupon: '',
    currency,
    discount:
      discountWithVat > 0 ? removeVat(discountWithVat, vatRate, true, 2) : 0,
    index,
    item_brand: brandName,
    item_category: category,
    item_category2: category2,
    item_list_id: itemListId,
    item_list_name: itemListName,
    item_variant: itemVariant,
    location_id: '',
    price: round(finalPriceWithoutVat, 4),
    quantity,
    param1,
  };

  if (prop(additionalData, 'includeNotRoundedPrice')) {
    finalObject.notRoundedPrice = finalPriceWithoutVat;
  }

  const parentCategories =
    prop(product, 'parent_categories') ||
    prop(product, 'parent_categories') ||
    prop(product, 'categories_names');
  const itemCategory1 =
    prop(parentCategories, '0.category_name') ||
    prop(product, 'categories_names.0') ||
    prop(savedProduct, 'category') ||
    '';
  const itemCategory2 =
    prop(parentCategories, '1.category_name') ||
    prop(product, 'categories_names.1') ||
    prop(savedProduct, 'category2') ||
    '';
  const itemCategory3 =
    prop(parentCategories, '2.category_name') ||
    prop(product, 'categories_names.2') ||
    prop(savedProduct, 'category3') ||
    '';
  const itemCategory4 =
    prop(parentCategories, '3.category_name') ||
    prop(product, 'categories_names.3') ||
    prop(savedProduct, 'category4') ||
    '';
  const itemCategory5 =
    prop(parentCategories, '4.category_name') ||
    prop(product, 'categories_names.4') ||
    prop(savedProduct, 'category5') ||
    '';
  if (!finalObject.item_category && itemCategory1) {
    finalObject.item_category = itemCategory1;
  }
  if (!finalObject.item_category2 && itemCategory2) {
    finalObject.item_category2 = itemCategory2;
  }
  if (!finalObject.item_category3 && itemCategory3) {
    finalObject.item_category3 = itemCategory3;
  }
  if (!finalObject.item_category4 && itemCategory4) {
    finalObject.item_category4 = itemCategory4;
  }
  if (!finalObject.item_category5 && itemCategory5) {
    finalObject.item_category5 = itemCategory5;
  }

  return finalObject;
};

export const prepareProductDetailForMarketing = (
  affiliation: string,
  product: any,
  dataLayerProducts?: any,
  additionalData?: {
    item_list_id?: string;
    item_list_name?: string;
    item_variant?: string;
    location_id?: string;
    quantity?: number;
    idx?: number;
    good?: any;
  },
): any => {
  const finalItem = prepareProductForMarketing(
    affiliation,
    product,
    dataLayerProducts,
    additionalData,
  );

  if (!finalItem) {
    return null;
  }

  return {
    currency: finalItem.currency,
    value: round(finalItem.price, 4),
    items: [finalItem],
  };
};
